import { useState } from 'react';
import { api } from '@/lib/api-client';
import { useAuthStore } from '@/store/authStore';
import type { TokenResponse } from '@/types/auth';

/**
 * Calls POST /api/auth/switch-tenant, replaces the current JWT in the auth store
 * with the new one (freshly scoped to the target tenant), and broadcasts a
 * `cbs:tenant-switched` window event so other parts of the app (Module B-07)
 * can clear their tenant-scoped caches.
 *
 * Throws on error so callers can react; also exposes the error message via
 * `error` for inline display.
 */
export function useSwitchTenant() {
  const storeLogin = useAuthStore((s) => s.login);
  const [isSwitching, setIsSwitching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const switchTo = async (tenantCod: string): Promise<void> => {
    setIsSwitching(true);
    setError(null);
    try {
      const data = await api.post<TokenResponse>('/api/auth/switch-tenant', {
        tenantCod,
      });
      storeLogin(data.user, data.accessToken, data.refreshToken);
      window.dispatchEvent(new CustomEvent('cbs:tenant-switched', { detail: { tenantCod } }));
    } catch (err: unknown) {
      const apiErr = err as { statusCode?: number; message?: string | string[] };
      const msg =
        typeof apiErr.message === 'string'
          ? apiErr.message
          : Array.isArray(apiErr.message)
            ? apiErr.message.join(', ')
            : 'Failed to switch tenant';
      setError(msg);
      throw err;
    } finally {
      setIsSwitching(false);
    }
  };

  return { switchTo, isSwitching, error };
}
